import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, AfterContentChecked } from '@angular/core';
import { catchError, filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { environment } from '@environments/environment';
import { CdkDragEnd, CdkDragMove, CdkDragStart } from '@angular/cdk/drag-drop';
import { forkJoin, Observable, Subject, takeUntil, of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { PDFDocumentProxy, ViewPortType, ScaleRegistry, ButtonStatus, SignAreaConfiguration, DocumentConfiguration, ZoomAction, ContentType, SignDocumentRequest, MessageType, SigntrustCertificate, PinMode, SignatureProfile, SignatureImage, SignatureLastPosition, InfoItems, PageType } from '@models/index.model';
import { SigntrustService, LoadingService, FeedMessageService, showError, showSuccess, DocumentConfigurationService, SigntrustUtilsService } from '@services/index.service';
import { DialogConfirmPinReasonComponent, DialogSelectCertificateComponent, EditSignatureConfigComponent } from '@pages/index.pages';
import { downloadFile, formatBytes, round } from '../../../utils/helpers';
import { ConfirmationDialog } from './common/confirmation-dialog.component';
import { DialogStaticNotificationsComponent } from './common/dialog-static-notifications.component';
import { DialogConfirmDownloadComponent } from './common/dialog-confirm-download.component';
import { DialogDocumentInfoComponent } from './dialog-document-info/dialog-document-info.component';
import html2canvas from 'html2canvas';

@Component({
  selector: 'signtrust-pdf-viewer',
  templateUrl: 'signtrust-pdf-viewer.component.html',
  styleUrls: ['./signtrust-pdf-viewer.component.scss'],
  standalone: false
})
export class SigntrustPdfViewerComponent implements OnInit, AfterContentChecked, OnDestroy {
  public isMobile: boolean;
  @Input() documentFile: File;
  @Input() page: number = 1;
  @Input() pages: number[] = [1];
  @Input() isSp: boolean;
  @Output() onClose = new EventEmitter<void>();
  @Output() spUploadDocument = new EventEmitter<File>();
  public theme = environment.THEME;
  // PDF CONFIG
  public hidePDFNotify: boolean = true;
  public hasSelectedCertificate: boolean = false;
  public pdfLoaded: boolean = false;
  public loadPreference: boolean = true;
  public loadingImg: boolean = true;
  public sendData: boolean = false;
  public showSignButton: boolean = false;
  public showPagination: boolean = true;
  public totalPages: number;
  public adjustViewPort: ViewPortType = ViewPortType.PAGE_FIT;
  public zoom: number = 1.0;
  public preventCloseMsg: string;
  private _fileHasChanges: boolean = false;
  // SIGN AREA CONFIG
  @ViewChild('inputPage') inputPage: ElementRef;
  @ViewChild('pdfToolbar') pdfToolbar: any;
  @ViewChild('pdfNotify') pdfNotify: any;
  @ViewChild('pdfViewer') pdfViewer: any;
  public dynamicPdfHeight: any;
  private _signDocumentRequest: SignDocumentRequest;
  private _documentScaleRegistry: ScaleRegistry;
  public signAreaInitialConfig: SignAreaConfiguration;
  // FLOATING MENU BUTTONS
  public signButtonStatus: ButtonStatus = ButtonStatus.ENABLED;
  public downloadButtonStatus: ButtonStatus = ButtonStatus.DISABLED;
  public beforeUnloadListener: any;
  public buttonStatus = ButtonStatus;
  public viewPortType = ViewPortType;
  public zoomAction = ZoomAction;
  public MIN_ZOOM: number = 0.8;
  public MAX_ZOOM: number = 5;
  public NO_ZOOM: number = 1;
  public docInfo: any;
  public documentProxy: PDFDocumentProxy;
  // SIGNATURE AREA
  @ViewChild('signArea') signArea: ElementRef;
  @ViewChild('signImg') signImg: ElementRef;
  loading: boolean;
  metadataCertificate: any;
  metadataSelection: any;
  useCustomImg: boolean = false;
  currentDate: Date;
  base64Image: string = undefined;
  pdfTopHeight: number;
  templateImageFile: any = null;
  public defaultSignPosition: SignatureLastPosition = {
    x: 0,
    y: 0,
    width: 300,
    height: 75,
    rotationDegrees: 0,
  };
  public userSignPosition: SignatureLastPosition | null = null;
  private _lastPositionLoadedOnce: boolean = false;
  private _lastPositionDirty: boolean = false;
  public defaultSignConfig = {
    certificate: {
      certSelect: false,
      certSelectData: { certId: null }
    },
    template: {
      useCustomImg: false,
      useCustomInfo: true,
      pagesData: { pageType: 'CURRENT', pageRange: undefined as any },
      customInfoData: [
        { key: 'certificate', check: true, name: 'Certificado', disabled: true },
        { key: 'friendlyName', check: true, name: 'Firmado por', disabled: false },
        { key: 'date', check: true, name: 'Fecha y hora', disabled: false },
        { key: 'thumbprint', check: true, name: 'Huella digital', disabled: false }
      ]
    }
  };
  public userSignConfig: any;
  pageType: PageType = PageType.CURRENT;
  username: any;
  get signAreaElement(): HTMLElement {
    return this.signArea.nativeElement;
  }
  @ViewChild('confirmEditButtons') confirmEditButtons: ElementRef;
  get confirmEditButtonsElement(): HTMLElement {
    return this.confirmEditButtons.nativeElement;
  }
  get buttonsRect(): DOMRect {
    return this.confirmEditButtonsElement?.getBoundingClientRect();
  }
  public dragZoneCoord: { x: number; y: number };
  public dragZoneDimension: { width: number; height: number };
  public documentRect: DOMRect;
  public currentScale: number;
  public previousScale: number;
  public dragBoundaryWidth: number = 0;
  public dragBoundaryHeight: number = 0;
  public buttonsPosition: string = 'confirm-cancel-bottom-left';
  private _destroy$ = new Subject<void>();
  public pdfToolbarHeight: number;
  public pdfNotifyHeight: number;
  public pdfViewerHeight: number;
  public selectCertificate: SigntrustCertificate;
  public certificateList: SigntrustCertificate[];
  public defaultImageFile: File;
  public pagesRange: any;
  public currentRotation: number = 0;
  public currentLang: string;

  // SIGNTRUST PROFILE (new endpoints)
  public signatureProfile: SignatureProfile | null = null;

  public constructor(
    private _notify: FeedMessageService,
    private _signtrustServ: SigntrustService,
    private _translate: TranslateService,
    private _loading: LoadingService,
    private _dialog: MatDialog,
    private _documentConfig: DocumentConfigurationService,
    private _utils: SigntrustUtilsService,
    private _cdRef: ChangeDetectorRef
  ) {
    this.isMobile = environment.platform === 'mobile';
    this.currentLang = this._translate.currentLang;
    _translate.get(['WEB-SIGN.SIGNTRUST-LIB.ERRORS.PREVENT-CLOSE']).subscribe(res => {
      this.preventCloseMsg = res['WEB-SIGN.SIGNTRUST-LIB.ERRORS.PREVENT-CLOSE'];
    });
    this.theme = this._utils.titleCaseWord(this.theme);
  }

  public ngOnInit(): void {
    if (!this.documentFile) {
      this.onCloseViewer();
    } else {
      this._translate.get(['WEB-SIGN.SIGN-IMG.LABELS.SIGN-METADATA-LIST']).subscribe(res => {
        this.defaultSignConfig.template.customInfoData[0].name = res['WEB-SIGN.SIGN-IMG.LABELS.SIGN-METADATA-LIST']['CERTIFICATE'];
        this.defaultSignConfig.template.customInfoData[1].name = res['WEB-SIGN.SIGN-IMG.LABELS.SIGN-METADATA-LIST']['SIGN-BY'];
        this.defaultSignConfig.template.customInfoData[2].name = res['WEB-SIGN.SIGN-IMG.LABELS.SIGN-METADATA-LIST']['DATE'];
        this.defaultSignConfig.template.customInfoData[3].name = res['WEB-SIGN.SIGN-IMG.LABELS.SIGN-METADATA-LIST']['THUMBPRINT'];
      });
      this._getPreloadedCertificates();
    }
    this.beforeUnloadListener = event => {
      event.returnValue = this.preventCloseMsg;
      return this.preventCloseMsg;
    };
  }

  public ngAfterContentChecked() {
    this.recalculatePdfViewport();
  }

  public _getPreloadedCertificates(): void {
    this._signtrustServ
      .getSigntrustCertificates()
      .pipe(map(res => res.data))
      .subscribe((certificateList: SigntrustCertificate[]) => {
        this.certificateList = certificateList;
        if (this.certificateList.length > 0) {
          this._loadSignatureConfiguration();
        } else {
          this._notify.feedBackResult(MessageType.ERROR, this._translate.instant('WEB-SIGN.SIGNTRUST-LIB.ERRORS.NO-CERTIFICATES'), showError);
          this.loadPreference = false;
        }
      }, error => {
        console.error(error);
        this._notify.feedBackResult(MessageType.ERROR, this._translate.instant('WEB-SIGN.SIGNTRUST-LIB.ERRORS.NO-CERTIFICATES'), showError);
        this.loadPreference = false;
      });
  }

  private _loadSignatureConfiguration(): void {
    forkJoin({
      profile: this._signtrustServ.getSignatureProfile().pipe(map(r => r.data), catchError(() => of(null))),
      image: this._signtrustServ.getSignatureImage().pipe(map(r => r.data), catchError(() => of(null))),
    }).subscribe(({ profile, image }) => {
      if (!profile) {
        const defaultProfile = this._buildDefaultSignatureProfile();
        this._signtrustServ.setSignatureProfile(defaultProfile).pipe(
          switchMap(() => this._signtrustServ.getSignatureProfile()),
          map(r => r.data),
          catchError(() => of(defaultProfile))
        ).subscribe(saved => {
          this.signatureProfile = saved;
          this._applySignatureProfile(saved, image);
        }, err => {
          this.loadPreference = false;
          this.openNotify();
          this.showSignButton = true;
          console.error(err);
        });
      } else {
        this.signatureProfile = profile;
        this._applySignatureProfile(profile, image);
      }
    }, err => {
      this.loadPreference = false;
      this.openNotify();
      this.showSignButton = true;
      console.error(err);
    });
  }

  private _buildDefaultSignatureProfile(): SignatureProfile {
    return {
      hasSelectedCert: false,
      selectedCertId: null,
      includeImage: false,
      includeInfo: true,
      infoItems: [InfoItems.CERTIFICATE, InfoItems.FRIENDLYNAME, InfoItems.DATE, InfoItems.THUMBPRINT],
      pageType: PageType.CURRENT,
      pageRange: undefined as any,
    };
  }

  private _applySignatureProfile(profile: SignatureProfile, image: SignatureImage | null): void {
    this.userSignConfig = this._mapSignatureProfileToUiConfig(profile);
    // image values
    if (profile.includeImage) {
      const img64 = image?.imageInBase64 || undefined;
      this.base64Image = img64 ? this._normalizeToDataUrl(img64) : undefined;
      this.useCustomImg = true;
    } else {
      this.base64Image = undefined;
      this.useCustomImg = false;
    }
    // certificate selection
    this._applyCertificateSelection(profile);
    // metadata
    this.metadataCertificate = profile.includeInfo;
    this._translate.get(['WEB-SIGN.SIGN-IMG.LABELS.SIGN-METADATA-LIST']).subscribe(res => {
      this.userSignConfig.template.customInfoData[0].name = res['WEB-SIGN.SIGN-IMG.LABELS.SIGN-METADATA-LIST']['CERTIFICATE'];
      this.userSignConfig.template.customInfoData[1].name = res['WEB-SIGN.SIGN-IMG.LABELS.SIGN-METADATA-LIST']['SIGN-BY'];
      this.userSignConfig.template.customInfoData[2].name = res['WEB-SIGN.SIGN-IMG.LABELS.SIGN-METADATA-LIST']['DATE'];
      this.userSignConfig.template.customInfoData[3].name = res['WEB-SIGN.SIGN-IMG.LABELS.SIGN-METADATA-LIST']['THUMBPRINT'];
    });
    this.metadataSelection = this.userSignConfig.template.customInfoData;
    // pages
    this.pageType = profile.pageType as unknown as PageType;
    this.pagesRange = profile.pageRange;
    this.openNotify();
    if (profile.includeInfo || profile.includeImage) {
      this.showSignButton = false;
      if (this.documentRect) {
        this.pagesSignTypeChange(this.pageType);
      } else {
        setTimeout(() => this.pagesSignTypeChange(this.pageType), 1000);
      }
      this.initVisualSignConfig();
    } else {
      this.showSignButton = true;
      this.loadPreference = false;
    }
  }

  private _applyCertificateSelection(profile: SignatureProfile): void {
    if (profile.hasSelectedCert && profile.selectedCertId) {
      const found = this.certificateList?.find(c => c.certificateHandler === profile.selectedCertId);
      if (found) {
        this.selectCertificate = found;
        this.hasSelectedCertificate = true;
        return;
      }
    }
    this.selectCertificate = null;
    this.hasSelectedCertificate = false;
    if (this.certificateList?.length === 1) {
      this.selectCertificate = this.certificateList[0];
      this.hasSelectedCertificate = true;
    }
  }

  private _normalizeToDataUrl(value: string): string {
    if (!value) return undefined;
    if (value.startsWith('data:')) return value;
    return `data:image/png;base64,${value}`;
  }

  private _mapSignatureProfileToUiConfig(profile: SignatureProfile): any {
    const ui = this._utils.cloneObj(this.defaultSignConfig);
    ui.template.useCustomImg = !!profile.includeImage;
    ui.template.useCustomInfo = !!profile.includeInfo;
    ui.template.pagesData.pageType = profile.pageType;
    ui.template.pagesData.pageRange = profile.pageRange;
    const items = new Set(profile.infoItems || []);
    ui.template.customInfoData[0].check = items.has(InfoItems.CERTIFICATE);
    ui.template.customInfoData[1].check = items.has(InfoItems.FRIENDLYNAME);
    ui.template.customInfoData[2].check = items.has(InfoItems.DATE);
    ui.template.customInfoData[3].check = items.has(InfoItems.THUMBPRINT);
    ui.certificate.certSelect = !!profile.hasSelectedCert;
    ui.certificate.certSelectData.certId = profile.selectedCertId ?? null;
    return ui;
  }

  private _isFiniteNumber(v: any): v is number {
    return typeof v === 'number' && Number.isFinite(v);
  }

  private _getSafeLastPosition(raw: any): SignatureLastPosition | null {
    if (!raw || typeof raw !== 'object') return null;
    const x = raw.x;
    const y = raw.y;
    const width = raw.width;
    const height = raw.height;
    const rotationDegrees = raw.rotationDegrees ?? 0;
    if (
      !this._isFiniteNumber(x) ||
      !this._isFiniteNumber(y) ||
      !this._isFiniteNumber(width) ||
      !this._isFiniteNumber(height) ||
      !this._isFiniteNumber(rotationDegrees)
    ) {
      return null;
    }
    if (width <= 0 || height <= 0) return null;
    const minW = 60;
    const minH = 60;
    const safe: SignatureLastPosition = {
      x,
      y,
      width: Math.max(width, minW),
      height: Math.max(height, minH),
      rotationDegrees: ((rotationDegrees % 360) + 360) % 360,
    };
    if (this.documentRect?.width && this.documentRect?.height) {
      const maxX = Math.max(0, Math.round(this.documentRect.width - safe.width));
      const maxY = Math.max(0, Math.round(this.documentRect.height - safe.height));
      safe.x = Math.min(Math.max(0, Math.round(safe.x)), maxX);
      safe.y = Math.min(Math.max(0, Math.round(safe.y)), maxY);
    } else {
      safe.x = Math.max(0, Math.round(safe.x));
      safe.y = Math.max(0, Math.round(safe.y));
    }
    return safe;
  }

  private _buildCenteredDefaultPosition(): SignatureLastPosition {
    const pos: SignatureLastPosition = { ...this.defaultSignPosition };
    if (this.documentRect?.width && this.documentRect?.height) {
      pos.x = Math.round((this.documentRect.width / 2) - (pos.width / 2));
      pos.y = Math.round((this.documentRect.height / 2) - (pos.height / 2));
      pos.x = Math.max(0, pos.x);
      pos.y = Math.max(0, pos.y);
    }
    return pos;
  }

  public initVisualSignConfig() {
    if (!(this.signatureProfile?.includeInfo || this.signatureProfile?.includeImage || this.metadataCertificate || this.useCustomImg)) {
      this.loadPreference = false;
      return;
    }
    if (this._lastPositionLoadedOnce && this._lastPositionDirty) {
      this.loadPreference = false;
      return;
    }
    this._signtrustServ.getSignatureLastPosition().pipe(
      map(r => r.data),
      catchError(() => of(null))
    ).subscribe((pos) => {
      const safe = this._getSafeLastPosition(pos);
      if (safe) {
        this.userSignPosition = safe;
        this._applyPositionToUi(safe, true);
      } else {
        this._initDefaultPositionOnly();
      }
    });
  }

  private _initDefaultPositionOnly(): void {
    const fallback = this._buildCenteredDefaultPosition();
    this.userSignPosition = fallback;
    this._applyPositionToUi(fallback, true);
  }

  private _applyPositionToUi(pos: SignatureLastPosition, resetDirty: boolean): void {
    this.fixPdfViewport();
    this.loadingImg = true;
    this.dragZoneCoord = { x: pos.x, y: pos.y };
    this.dragZoneDimension = { width: pos.width, height: pos.height };
    this.currentRotation = (pos.rotationDegrees || 0) % 360;
    this._lastPositionLoadedOnce = true;
    if (resetDirty) this._lastPositionDirty = false;
    if (this.templateImageFile) {
      this.loadingImg = true;
      this.rotateImage(this.templateImageFile, this.currentRotation);
    }
    this._documentConfig.configurationChange$().pipe(takeUntil(this._destroy$)).subscribe((config: DocumentConfiguration) => {
      this._updateSignArea(config);
    });
    this.username = this._utils.getLocalStorage('PersonalUserSession')?.userData?.friendlyName;
    this.currentDate = new Date();
    setTimeout(() => {
      this.screenshot();
      setTimeout(() => {
        if (this.templateImageFile) {
          this.loadingImg = true;
          this.rotateImage(this.templateImageFile, this.currentRotation);
        }
      }, 1000);
    }, 300);

    this.loadPreference = false;
  }

  private _buildCurrentUiPositionCandidate(): SignatureLastPosition {
    const base = this.userSignPosition ?? this._buildCenteredDefaultPosition();
    return {
      x: this.dragZoneCoord?.x ?? base.x,
      y: this.dragZoneCoord?.y ?? base.y,
      width: this.dragZoneDimension?.width ?? base.width,
      height: this.dragZoneDimension?.height ?? base.height,
      rotationDegrees: this.currentRotation ?? base.rotationDegrees,
    };
  }

  private _persistLastPositionAfterSuccessfulSign(): Observable<SignatureLastPosition | null> {
    const candidate = this._buildCurrentUiPositionCandidate();
    const safe = this._getSafeLastPosition(candidate) ?? this._buildCenteredDefaultPosition();
    this.userSignPosition = safe;
    return this._signtrustServ.setSignatureLastPosition(safe).pipe(
      switchMap(() => this._signtrustServ.getSignatureLastPosition()),
      map(r => this._getSafeLastPosition(r.data)),
      catchError(() => of(null))
    );
  }

  private fixPdfViewport() {
    if (this.documentRect) {
      if (this.documentRect.width < this.documentRect.height) {
        this.adjustViewPort = ViewPortType.PAGE_HEIGHT;
        setTimeout(() => {
          this.adjustViewPort = ViewPortType.PAGE_FIT;
        }, 100);
      } else if (this.documentRect.height < this.documentRect.width) {
        this.adjustViewPort = ViewPortType.PAGE_WIDTH;
        setTimeout(() => {
          this.adjustViewPort = ViewPortType.PAGE_FIT;
        }, 100);
      }
    }
  }

  private rotateImage(srcBase64, degrees) {
    const canvas = document.createElement('canvas');
    let ctx = canvas.getContext('2d');
    let image = new Image();
    image.onload = () => {
      canvas.width = degrees % 180 === 0 ? image.width : image.height;
      canvas.height = degrees % 180 === 0 ? image.height : image.width;
      ctx?.translate(canvas.width / 2, canvas.height / 2);
      ctx?.rotate((degrees * Math.PI) / 180);
      ctx?.drawImage(image, image.width / -2, image.height / -2);
      this.setImageBase64Src(canvas.toDataURL('image/png'));
      this.loadingImg = false;
    };
    image.onerror = () => {
      this.loadingImg = false;
    };
    image.src = srcBase64;
  }

  public async screenshot(type?) {
    let captureElement: any = document.querySelector('#imgTemplate');
    if (!this.base64Image && !this.useCustomImg) {
      captureElement = document.querySelector('.metadata-template-content');
    }
    if (!captureElement) {
      this.loadingImg = false;
      return;
    }
    this.loadingImg = true;
    try {
      const canvas = await html2canvas(captureElement, {
        backgroundColor: null,
        useCORS: true,
        allowTaint: true,
        scale: 2,
        logging: false,
        removeContainer: true,
      });
      canvas.getContext('2d', { willReadFrequently: true } as any);
      const dataUrl = canvas.toDataURL('image/png');
      this.templateImageFile = (dataUrl && dataUrl !== 'data:,') ? dataUrl : null;
      if (!this.templateImageFile) {
        this.loadingImg = false;
        console.error('No se pudo obtener la imagen de firma visual');
        return;
      }
      if (this.currentRotation) {
        this.rotateImage(this.templateImageFile, this.currentRotation);
      } else {
        this.loadingImg = false;
      }
      if (type === 'signing') {
        setTimeout(() => this.signPdfWithArea(), 300);
      }
    } catch (e) {
      console.error('Error screenshot/html2canvas', e);
      this.templateImageFile = null;
      this.loadingImg = false;
    }
  }

  public openNotify() {
    this.hidePDFNotify = false;
    setTimeout(() => {
      this.fixPdfViewport();
    }, 100);
  }
  public closeNotify() {
    this.hidePDFNotify = true;
    setTimeout(() => {
      this.fixPdfViewport();
    }, 100);
  }

  private getImageBase64Src(): string {
    const imageTag = document.getElementById('signImg');
    return imageTag?.attributes.getNamedItem('src')?.value || '';
  }
  private setImageBase64Src(imgBase64: string) {
    const imageTag = document.getElementById('signImg');
    return imageTag?.setAttribute('src', imgBase64);
  }

  public rotate(rotation: number) {
    this.loadingImg = true;
    this._lastPositionDirty = true;
    this.currentRotation = (this.currentRotation + rotation) % 360;
    this.updateRotationDimensions();
    this.rotateImage(this.templateImageFile, this.currentRotation);
    this.updateSignAreaPosition();
  }

  public updateRotationDimensions(): void {
    if (this.currentRotation % 180 === 90 || this.currentRotation % 180 === 0) {
      this.dragZoneDimension = {
        width: this.dragZoneDimension.height,
        height: this.dragZoneDimension.width
      };
      this._cdRef.detectChanges();
    }
  }

  public recalculatePdfViewport() {
    if (this.pdfToolbar && this.pdfViewer) {
      if (this.pdfNotify) {
        this.pdfToolbarHeight = this.pdfToolbar?._elementRef?.nativeElement?.offsetHeight;
        this.pdfNotifyHeight = this.pdfNotify?.nativeElement?.offsetHeight;
        this.pdfViewerHeight = this.pdfViewer?.element?.nativeElement?.offsetHeight;
        this.pdfTopHeight = this.pdfToolbarHeight + this.pdfNotifyHeight;
        const newHeight = `calc(100vh - ${this.pdfTopHeight}px) !important`;
        this.dynamicPdfHeight = newHeight;
      } else {
        this.pdfToolbarHeight = this.pdfToolbar?._elementRef?.nativeElement?.offsetHeight;
        this.pdfViewerHeight = this.pdfViewer?.element?.nativeElement?.offsetHeight;
        this.pdfTopHeight = this.pdfToolbarHeight;
        const newHeight = `calc(100vh - ${this.pdfTopHeight}px) !important`;
        this.dynamicPdfHeight = newHeight;
      }
    }
    const canvasWrapperRect = document.querySelector('.canvasWrapper');
    if (canvasWrapperRect) {
      this.documentRect = canvasWrapperRect.getBoundingClientRect();
    }
    this._cdRef.detectChanges();
  }

  private _hasLiveDragState(): boolean {
    return !!this.dragZoneCoord && !!this.dragZoneDimension;
  }

  private _refreshVisualTemplateKeepLivePosition(): void {
    this.username = this._utils.getLocalStorage('PersonalUserSession')?.userData?.friendlyName;
    this.currentDate = new Date();
    setTimeout(() => this.screenshot(), 0);
  }

  public onSelectCertificate(type) {
    if (this.selectCertificate) {
      if (type === 'signing') {
        this._loading.showGlobalLoading();
        this.sendData = true;
        setTimeout(() => {
          this.screenshot(type);
        }, 1000);
      } else if (type === 'no-visual') {
        this._loading.showGlobalLoading();
        this.sendData = true;
        this.signPdfWithoutArea();
      } else if (type === 'current') {
        const dialogRef = this._dialog.open(DialogSelectCertificateComponent, {
          width: '900px',
          disableClose: true,
          data: {
            type: type,
            selectCertificate: this.selectCertificate
          },
        });
        return dialogRef.afterClosed().subscribe(res => {
          if (res) {
            this.selectCertificate = res.selectCertificate;
            this.hasSelectedCertificate = true;
            if (this._lastPositionDirty || this._hasLiveDragState()) {
              this._refreshVisualTemplateKeepLivePosition();
            } else if (this.userSignPosition) {
              this._applyPositionToUi(this.userSignPosition, false);
            } else {
              this.initVisualSignConfig();
            }
          }
        });
      }
    } else {
      const dialogRef = this._dialog.open(DialogSelectCertificateComponent, {
        width: '900px',
        disableClose: true,
        data: {
          type: type,
          selectCertificate: this.selectCertificate
        },
      });
      return dialogRef.afterClosed().subscribe(res => {
        if (res) {
          this.selectCertificate = res.selectCertificate;
          this.hasSelectedCertificate = true;
          if (res.type === 'signing') {
            if (this.selectCertificate) {
              setTimeout(() => {
                this.screenshot(res.type);
              }, 1000);
            }
          } else if (type === 'no-visual') {
            this.signPdfWithoutArea();
          } else if (this._lastPositionDirty || this._hasLiveDragState()) {
            this._refreshVisualTemplateKeepLivePosition();
          } else if (this.userSignPosition) {
            this._applyPositionToUi(this.userSignPosition, false);
          } else {
            this.initVisualSignConfig();
          }
        }
      });
    }
  }

  public onEditSignature() {
    const dialogRef = this._dialog.open(EditSignatureConfigComponent, {
      width: '900px',
      disableClose: true,
      data: {
        pages: this.pages,
        currentPage: this.page,
        totalPages: this.totalPages,
        userSignConfig: this.userSignConfig,
        selectCertificate: this.selectCertificate,
        defaultImageFile: this.defaultImageFile,
        signatureProfile: this.signatureProfile,
      },
    });

    return dialogRef.afterClosed().subscribe(res => {
      if (!res) return;
      this.selectCertificate = res.selectCertificate;
      this.userSignConfig = res.userSignConfig;
      if (res.signatureProfile) this.signatureProfile = res.signatureProfile;
      this.pageType = this.userSignConfig.template.pagesData.pageType;
      this.pagesSignTypeChange(this.pageType);
      const shouldHaveVisualTemplate = !!this.userSignConfig.template.useCustomImg || !!this.userSignConfig.template.useCustomInfo;
      if (this.userSignConfig.template.useCustomImg) {
        this.defaultImageFile = res.defaultImageFile ?? this.defaultImageFile;
        if (this.defaultImageFile) {
          this._utils.fileToBase64(this.defaultImageFile).subscribe((customImg64?: string) => {
            this.base64Image = customImg64 || undefined;
            setTimeout(() => this.screenshot(), 0);
          });
        } else if (res.signatureImage?.imageInBase64) {
          this.base64Image = this._normalizeToDataUrl(res.signatureImage.imageInBase64);
          setTimeout(() => this.screenshot(), 0);
        } else {
          this.base64Image = undefined;
          setTimeout(() => this.screenshot(), 0);
        }
      } else {
        this.base64Image = undefined;
        if (this.userSignConfig.template.useCustomInfo) {
          setTimeout(() => this.screenshot(), 0);
        } else {
          this.templateImageFile = null;
        }
      }
      this.useCustomImg = this.userSignConfig.template.useCustomImg;
      if (res.type === 'SAVE') {
        this._loadSignatureConfiguration();
      } else if (shouldHaveVisualTemplate) {
        this.showSignButton = false;
        if (!this.userSignPosition) {
          this.initVisualSignConfig();
        }
      }
    });
  }

  public firstPage() {
    this.page = 1;
    if (this.pageType === PageType.CURRENT) {
      this.pagesSignTypeChange(this.pageType);
    }
  }

  public prevPage() {
    const newPage = this.page - 1;
    if (!newPage || newPage < 1) {
      this.page = 1;
    } else if (newPage >= this.totalPages) {
      this.page = this.totalPages;
    } else {
      this.page = newPage;
    }
    if (this.pageType === PageType.CURRENT) {
      this.pagesSignTypeChange(this.pageType);
    }
  }

  public nextPage() {
    const newPage = this.page + 1;
    if (!newPage || newPage < 1) {
      this.page = 1;
    } else if (newPage >= this.totalPages) {
      this.page = this.totalPages;
    } else {
      this.page = newPage;
    }
    if (this.pageType === PageType.CURRENT) {
      this.pagesSignTypeChange(this.pageType);
    }
  }

  public lastPage() {
    this.page = this.totalPages;
    if (this.pageType === PageType.CURRENT) {
      this.pagesSignTypeChange(this.pageType);
    }
  }

  public pagesSignTypeChange(pageType: PageType): void {
    switch (pageType) {
      case PageType.CURRENT:
        this.pages = [this.page];
        this.page = this.pages[0];
        this._cdRef.detectChanges();
        break;
      case PageType.LAST:
        this.pages = [this.totalPages];
        this.page = this.pages[0];
        this._cdRef.detectChanges();
        break;
      case PageType.ALL:
        this.pages = [];
        for (let index = 1; index <= this.totalPages; index++) {
          if (this.pages.indexOf(index) == -1) this.pages.push(index);
          if (index === this.totalPages) {
            this.page = this.pages[0];
            this._cdRef.detectChanges();
          }
        }
        break;
      case PageType.RANGE:
        this.validatePagesRange(this.pagesRange);
        break;
    }
  }

  public validatePagesRange(pagesRange) {
    if (pagesRange != '' && this.totalPages) {
      try {
        const _pagesRange = this._utils.parsePageRange(pagesRange, this.totalPages);
        if (_pagesRange) {
          this.pages = _pagesRange;
          this.page = this.pages[0];
          this._cdRef.detectChanges();
        } else {
          this.pages = [1];
          this.page = 1;
          this._cdRef.detectChanges();
        }
      } catch {
        console.error('Invalid pagesRange: ', pagesRange);
      }
    }
  }

  public updateSignAreaPosition() {
    const config = {
      rect: this.documentRect,
      scale: this.currentScale,
      previousScale: this.previousScale
    };
    this._updateSignArea(config);
  }

  async getDocumentMetadata(pdf: PDFDocumentProxy) {
    const metadata: any = await pdf.getMetadata();
    const info: any = metadata.info ? metadata.info : metadata.arg.info;
    this.docInfo = {
      Name: this.documentFile.name,
      Size: formatBytes(this.documentFile.size),
      Title: info.Title,
      Author: info.Author,
      Creator: info.Creator,
      CreationDate: this._utils.parsePdfDate(info.CreationDate),
      ModDate: this._utils.parsePdfDate(info.ModDate),
      Producer: info.Producer,
      PDFFormatVersion: info.PDFFormatVersion,
      Language: info.Language,
      IsSignaturesPresent: info.IsSignaturesPresent,
    };
  }

  openDocumentInfoClick() {
    this._dialog.open(DialogDocumentInfoComponent, {
      width: '500px',
      disableClose: true,
      data: { docInfo: this.docInfo },
    });
  }

  public afterLoadComplete(pdf: PDFDocumentProxy): void {
    this.getDocumentMetadata(pdf);
    this.documentProxy = pdf;
    this.pdfLoaded = true;
    this.totalPages = pdf.numPages;
    if (this.totalPages) {
      this.pagesSignTypeChange(this.pageType);
    }
  }

  public pageRendered(event: any): void {
    this._documentScaleRegistry = {
      previousScale: this._documentScaleRegistry?.scale || undefined,
      scale: event.source.scale,
    };
    this._updateDocumentConfiguration({
      rect: document.querySelector('.canvasWrapper')!.getBoundingClientRect(),
      scale: this._documentScaleRegistry.scale,
      previousScale: this._documentScaleRegistry.previousScale,
    });
    const canvasWrapperRect = document.querySelector('.canvasWrapper');
    if (canvasWrapperRect) {
      this.documentRect = canvasWrapperRect.getBoundingClientRect();
    }
    this.updateSignAreaPosition();
  }

  private _updateDocumentConfiguration(config: DocumentConfiguration) {
    this._documentConfig.updateConfiguration(config);
  }

  public onError(error: any): void {
    this._notify.feedBackResult(MessageType.ERROR, error);
    console.error('ERROR: ', error);
    this.onCloseViewer();
  }

  public onSignDocument(signAreaConfig?: SignAreaConfiguration) {
    if (signAreaConfig && this.selectCertificate) {
      signAreaConfig.pages = this.pages;
      if ((this.selectCertificate.pinMode !== PinMode.NoPIN) || this.selectCertificate.needReason) {
        this._dialog.open(DialogConfirmPinReasonComponent, {
          width: '400px',
          disableClose: true,
          data: this.selectCertificate,
        })
        .afterClosed()
        .subscribe(result => {
          if (result) {
            this._signDocumentRequest = {
              certificateId: this.selectCertificate.certificateHandler,
              pin: result.pin,
              reason: result.reason,
              visualSignature: signAreaConfig
            };
            this._loading.showGlobalLoading();
            this.goToSignDocument(this._signDocumentRequest);
          } else {
            this.sendData = false;
            this._loading.hideGlobalLoading();
          }
        });
      } else {
        this._signDocumentRequest = {
          certificateId: this.selectCertificate.certificateHandler,
          pin: null,
          reason: null,
          visualSignature: signAreaConfig
        };
        this._loading.showGlobalLoading();
        this.goToSignDocument(this._signDocumentRequest);
      }
    } else if (!signAreaConfig && this.selectCertificate) {
      if ((this.selectCertificate.pinMode !== PinMode.NoPIN) || this.selectCertificate.needReason) {
        this._dialog.open(DialogConfirmPinReasonComponent, {
          width: '400px',
          disableClose: true,
          data: this.selectCertificate,
        })
        .afterClosed()
        .subscribe(result => {
          if (result) {
            this._signDocumentRequest = {
              certificateId: this.selectCertificate.certificateHandler,
              pin: result.pin,
              reason: result.reason
            };
            this._loading.showGlobalLoading();
            this.goToSignDocument(this._signDocumentRequest);
          } else {
            this.sendData = false;
            this._loading.hideGlobalLoading();
          }
        });
      } else {
        this._signDocumentRequest = {
          certificateId: this.selectCertificate.certificateHandler,
          pin: null,
          reason: null
        };
        this._loading.showGlobalLoading();
        this.goToSignDocument(this._signDocumentRequest);
      }
    }
  }

  private goToSignDocument(req: any) {
    this._signtrustServ.onSignDocument(req, this.documentFile)
      .pipe(
        tap(_ => this._notify.feedBackResult(
          MessageType.SUCCESS,
          this._translate.instant('WEB-SIGN.SIGNTRUST-LIB.SUCCESS.SIGN-DOCUMENT'),
          showSuccess
        )),
        finalize(() => {
          this.sendData = false;
          this._loading.hideGlobalLoading();
        })
      )
      .subscribe(res => {
        if (res.headers.get('keyfactor-warning')) {
          const customHeaderWarning = res.headers.get('keyfactor-warning');
          this.openStaticNotification(customHeaderWarning);
        }
        if (res.body) {
          window.addEventListener('beforeunload', this.beforeUnloadListener, { capture: true });
          const blob = new Blob([res.body], { type: ContentType.pdfType });
          const signedDocumentFile = new File([blob], this.documentFile.name, { type: ContentType.pdfType });
          this.documentFile = signedDocumentFile;
          this._fileHasChanges = true;
          this.downloadButtonStatus = ButtonStatus.ENABLED;
          const shouldPersistLastPos = !!req?.visualSignature && !!this.templateImageFile;
          const afterPersist = () => {
            this._loadSignatureConfiguration();
            if (this.isSp) {
              this.uploadDocument();
            } else {
              this.openForceDownload();
            }
          };
          if (shouldPersistLastPos) {
            this._persistLastPositionAfterSuccessfulSign().subscribe(() => {
              this._lastPositionDirty = false;
              afterPersist();
            });
          } else {
            afterPersist();
          }
        }
      });
  }

  public openStaticNotification(msg) {
    const dialogRef = this._dialog.open(DialogStaticNotificationsComponent, {
      width: '550px',
      data: { staticMessages: msg }
    });
    dialogRef.disableClose = true;
    dialogRef.afterClosed().subscribe(() => {});
  }

  public openForceDownload() {
    const dialogRef = this._dialog.open(DialogConfirmDownloadComponent, {
      width: '550px'
    });
    dialogRef.disableClose = true;
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.onDownloadDocumentButtonClick();
      }
    });
  }

  public onDownloadDocumentButtonClick(): void {
    window.removeEventListener('beforeunload', this.beforeUnloadListener, { capture: true });
    if (this.isMobile) {
      const reader = new FileReader();
      reader.readAsDataURL(this.documentFile);
      reader.onloadend = async () => {
        const base64String = reader.result as string;
        let status = await Filesystem.checkPermissions();
        const state = status.publicStorage;
        const lastPath = this.documentFile.name;
        if (state === 'denied') {
          status = await Filesystem.requestPermissions();
        }
        const directory =
          Capacitor.getPlatform() === 'ios'
            ? Directory.Documents
            : Directory.Cache;
        await Filesystem.appendFile({
          path: lastPath,
          directory: directory,
          data: base64String
        });
        const finalUri = await Filesystem.getUri({
          path: lastPath,
          directory: directory
        });
        try {
          Filesystem.writeFile({
            path: lastPath,
            directory: directory,
            data: base64String,
            recursive: true
          }).then(async writeFileResult => {
            if (writeFileResult.uri === null) {
              this._notify.feedBackResult(MessageType.ERROR, JSON.stringify(writeFileResult), showError);
            } else {
              this._fileHasChanges = false;
              const msg = `${this._translate.instant('WEB-SIGN.FILE-UPLOAD.DOCUMENT-DOWNLOAD-MSG')}${writeFileResult.uri}`;
              this._notify.feedBackResult(MessageType.SUCCESS, msg, showSuccess);
              const canShare = await Share.canShare();
              if (canShare.value) {
                Share.share({
                  url: writeFileResult.uri,
                  files: [writeFileResult.uri]
                }).catch(e => console.error('An error occurred during the download', e));
              }
            }
          }, (err) => {
            this._notify.feedBackResult(MessageType.ERROR, err, showError);
          });
        } catch (e) {
          this._notify.feedBackResult(MessageType.ERROR, e, showError);
        }
      };
    } else {
      downloadFile(this.documentFile)
        .pipe(take(1))
        .subscribe(_ => this._fileHasChanges = false);
    }
  }

  public uploadDocument() {
    this.spUploadDocument.emit(this.documentFile);
  }

  public onFileUploaded(file: File, isSp?: boolean) {
    try {
      this._setDocumentFile(file, isSp);
    } catch (error) {
      this._notify.feedBackResult(MessageType.ERROR, error.message);
    }
    this._loading.hideGlobalLoading();
  }

  private _setDocumentFile(documentFile: File, isSp?: boolean) {
    const bytesToMb: number = 0.00000095367432;
    if (documentFile.size * bytesToMb >= 50) {
      throw new Error(this._translate.instant('GLOBAL.ERRORS.FILESIZE50MB'));
    }
    const fileTypePDF: string = 'application/pdf';
    if (documentFile.type !== fileTypePDF) {
      throw new Error(this._translate.instant('GLOBAL.ERRORS.FILETYPEPDF'));
    }
    this.documentFile = documentFile;
    localStorage.setItem('docOpen', '1');
    this._lastPositionLoadedOnce = false;
    this._lastPositionDirty = false;
    this._getPreloadedCertificates();
  }

  private _updateSignArea(config: DocumentConfiguration) {
    if (config.rect) {
      this.documentRect = config.rect;
      this.dragBoundaryWidth = this.documentRect.width;
      this.dragBoundaryHeight = this.documentRect.height;
      this.currentScale = config.scale;
      this.previousScale = config.previousScale;
      if (this.dragZoneDimension) {
        this.dragZoneDimension = {
          width: Math.max(Math.min(this.dragZoneDimension.width, this.documentRect.width), 60),
          height: Math.max(Math.min(this.dragZoneDimension.height, this.documentRect.height), 60),
        };
      }
      if (this.dragZoneCoord && this.dragZoneDimension) {
        const newX = Math.max(Math.min(this.dragZoneCoord.x, this.documentRect.width - this.dragZoneDimension.width), 0);
        const newY = Math.max(Math.min(this.dragZoneCoord.y, this.documentRect.height - this.dragZoneDimension.height), 0);
        this.dragZoneCoord = { x: newX, y: newY };
      }
      this._cdRef.detectChanges();
    }
  }

  public onSignAreaDragStart(event: CdkDragStart): void {
    this.showPagination = false;
  }

  public onSignAreaDragEnd(event: CdkDragEnd): void {
    this.showPagination = true;
    this.dragZoneCoord = event.source.getFreeDragPosition();
    this._lastPositionDirty = true;
  }

  public calculateButtonsPosition() {
    const buttonsRect = { height: 80, width: 175 };
    const totalHeight: number = this.dragZoneDimension.height + buttonsRect.height;
    const buttonsRight: number = this.dragZoneCoord.x + buttonsRect.width;
    if (totalHeight <= this.dragBoundaryHeight) {
      if (this.dragZoneCoord.y >= buttonsRect.height) {
        if (buttonsRight <= this.dragBoundaryWidth) return 'confirm-cancel-top-left';
        return 'confirm-cancel-top-right';
      }
      if (this.dragZoneCoord.y < buttonsRect.height) {
        if (buttonsRight <= this.dragBoundaryWidth) return 'confirm-cancel-bottom-left';
        return 'confirm-cancel-bottom-right';
      }
    }
    if (totalHeight > this.dragBoundaryHeight && buttonsRight <= this.dragBoundaryWidth) {
      return 'confirm-cancel-inner-top-left';
    }
    return 'confirm-cancel-inner-top-right';
  }

  public onSignAreaResize(dragHandle: HTMLElement, $event: CdkDragMove<any>): void {
    dragHandle.style.transform = '';
    this.dragZoneDimension = this._calculatesignAreaDimension(dragHandle, $event);
    this._lastPositionDirty = true;
  }

  private _calculatesignAreaDimension(
    dragHandle: HTMLElement,
    $event: CdkDragMove<any>
  ): { width: number; height: number } {
    let signAreaElementRect: DOMRect = null;
    if (this.signArea?.nativeElement) {
      signAreaElementRect = this.signAreaElement.getBoundingClientRect();
    } else {
      setTimeout(() => {
        signAreaElementRect = this.signAreaElement.getBoundingClientRect();
      }, 400);
    }
    const dragRect: DOMRect = dragHandle.getBoundingClientRect();
    let width: number = Math.max(
      this._calculateSignAreaRawDimensions($event.pointerPosition.x, signAreaElementRect.left, dragRect.width / 2),
      60
    );
    let height: number = Math.max(
      this._calculateSignAreaRawDimensions($event.pointerPosition.y, signAreaElementRect.top, dragRect.height / 2),
      60
    );
    if (signAreaElementRect.left + width >= this.documentRect.right) {
      width = this.documentRect.right - signAreaElementRect.left;
    }
    if (signAreaElementRect.top + height >= this.documentRect.bottom) {
      height = this.documentRect.bottom - signAreaElementRect.top;
    }
    return { width, height };
  }

  private _calculateSignAreaRawDimensions(pointerPositionEnd: number, signAreaOffset: number, dragElementOffset: number): number {
    return round(pointerPositionEnd - signAreaOffset + dragElementOffset, 0);
  }

  public signPdfWithArea() {
    let imgToSign: string = '';
    if (this.templateImageFile) {
      this.loadingImg = true;
      this.rotateImage(this.templateImageFile, this.currentRotation);
      setTimeout(() => {
        imgToSign = this.getImageBase64Src();
        if (imgToSign) {
          let boxSize: { width: number; height: number } = this.signAreaElement.getBoundingClientRect();
          this.onSignDocument(this._calculateCurrentSignAreaCoord(this.dragZoneCoord, boxSize, this.currentScale, imgToSign));
        } else {
          setTimeout(() => {
            imgToSign = this.getImageBase64Src();
            if (imgToSign) {
              let boxSize: { width: number; height: number } = this.signAreaElement.getBoundingClientRect();
              this.onSignDocument(this._calculateCurrentSignAreaCoord(this.dragZoneCoord, boxSize, this.currentScale, imgToSign));
            }
          }, 300);
        }
      }, 300);
    }
  }

  public signPdfWithoutArea() {
    this.onSignDocument();
  }

  private _calculateCurrentSignAreaCoord(
    dragCoord: { x: number; y: number },
    boxSize: { width: number; height: number },
    scale: number,
    imgToSign: string
  ): SignAreaConfiguration {
    return {
      x: Math.round(dragCoord.x / scale),
      y: Math.round(dragCoord.y / scale),
      width: Math.max(Math.round(boxSize.width / scale), 60),
      height: Math.max(Math.round(boxSize.height / scale), 60),
      imageBase64: imgToSign.split(',').slice(1).join(','),
      pages: this.pages,
    };
  }

  public onCloseViewer() {
    if (!this._fileHasChanges) {
      localStorage.removeItem('appUrlOpen');
      localStorage.setItem('docOpen', '0');
      this.onClose.emit();
      return;
    }
    this._dialog.open(ConfirmationDialog, {
      width: '400px',
      disableClose: true,
      data: {
        title: this._translate.instant('WEB-SIGN.SIGNTRUST-LIB.ERRORS.PREVENT-CLOSE'),
        message: this._translate.instant('WEB-SIGN.SIGNTRUST-LIB.ERRORS.PREVENT-CLOSE-MSG'),
      },
    })
    .afterClosed().pipe(filter((result: boolean) => result)).subscribe(() => {
      window.removeEventListener('beforeunload', this.beforeUnloadListener, { capture: true });
      localStorage.removeItem('appUrlOpen');
      this.onClose.emit();
    });
  }

  ngOnDestroy() {
    localStorage.removeItem('appUrlOpen');
    this._destroy$.next();
    this._destroy$.complete();
  }
}
